import { Modal, Col, Menu, Row, Space, Typography, Dropdown, message } from "antd";
import { useState } from "react";
import { Guid } from "guid-typescript";
import AssignmentGridView from "../components/assignments/AssignmentGridView";
import AssignmentFormDialog from "../components/assignments/AssignmentFormDialog";
import AssignMaterialFormDialog from "../components/wellbeing-corner/AssignMaterialFormDialog";
import MaterialTargetingPreview from "../components/wellbeing-corner/MaterialTargetingPreview";
import { AssignmentTarget } from "../components/assignments/AssignmentGridView.default";
import { SearchQuery } from "../code/search-query/search-query";
import {
  getMaterialCompanyAssignmentsExport,
  getMaterialDepartmentAssignmentsExport,
  getMaterialUserAssignmentsExport,
} from "../repository/data.repository";
import Loader from "../controls/Loader";
import { useSelector } from "../store/store";
import { authorizeSelectors } from "../store/authorize";
import { getProblemMessage } from "../utils/http";

const exportFns: Record<AssignmentTarget, (input: any) => Promise<any>> = {
  company: getMaterialCompanyAssignmentsExport,
  department: getMaterialDepartmentAssignmentsExport,
  user: getMaterialUserAssignmentsExport,
};

function WellbeingMaterialAssignmentsPage() {
  const permissions = useSelector(authorizeSelectors.permissions);
  const [modal, contextHolder] = Modal.useModal();
  const [searchQuery, setSearchQuery] = useState<SearchQuery>({
    conditions: [],
  });
  const [selectedKey, setSelectedKey] = useState<AssignmentTarget>("company");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogRecord, setDialogRecord] = useState<any>({});
  const [assignOpen, setAssignOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [triggerView, setTriggerView] = useState(Guid.create().toString());

  const items = [
    { key: "company", label: "Company Assignments" },
    { key: "department", label: "Department Assignments" },
    { key: "user", label: "User Assignments" },
  ];

  function onSelect(e: { readonly key: string }) {
    setSelectedKey(e.key as AssignmentTarget);
  }

  function onSearch(query: SearchQuery): void {
    setSearchQuery(query);
  }

  function onAction(key: string, record: any): void {
    if (key === "delete") {
      setDialogRecord(record);
      setDialogOpen(true);
    }
  }

  function onFinish(): void {
    setDialogOpen(false);
    setDialogRecord({});
    setTriggerView(Guid.create().toString());
  }

  function onClose(): void {
    setDialogOpen(false);
    setDialogRecord({});
  }

  function onAssignFinish(): void {
    setAssignOpen(false);
    setTriggerView(Guid.create().toString());
  }

  function onAssignClose(): void {
    setAssignOpen(false);
  }

  function onDownloadExport(): void {
    const loading = modal.info({
      content: (
        <Typography.Text className="m-1 ml-2 inline-block">
          Downloading assignments export file..
        </Typography.Text>
      ),
      footer: null,
      centered: true,
      icon: <Loader />,
    });

    exportFns[selectedKey]({
      searchQuery,
      pageNumber: 0,
      pageSize: Number.MAX_SAFE_INTEGER,
    })
      .then(() => loading.destroy())
      .catch((err) => {
        loading.destroy();
        message.error(getProblemMessage(err) || "Failed to download export.");
      });
  }

  return (
    <Col className="relative flex flex-auto flex-col overflow-hidden overflow-y-auto">
      <Row className="sticky top-0 z-10 flex flex-row flex-wrap items-center p-4">
        <Typography.Title className="m-0 flex-auto" level={4}>
          Assignments &amp; Targeting
        </Typography.Title>
        <Space className="flex flex-row">
          <Dropdown.Button
            type="primary"
            disabled={!permissions.can("WellbeingCornerAssignment", "create")}

            className={`${!permissions.can("WellbeingCornerAssignment", "create") ? "disabled" : ""}`}
            menu={{
              items: [
                {
                  key: "previewTargets",
                  label: "Preview Targets",
                  onClick: () => setPreviewOpen(true),
                },
                {
                  key: "downloadExport",
                  label: "Export to Excel",
                  disabled: !permissions.can("WellbeingCornerAssignment", "view"),
                  onClick: onDownloadExport,
                },
              ],
            }}
            onClick={() => setAssignOpen(true)}
          >
            Assign Material
          </Dropdown.Button>
        </Space>
      </Row>
      <Row className="flex flex-auto flex-row flex-nowrap" style={{ minHeight: 360 }}>
        <Col className="flex flex-col pb-4">
          <Menu
            className="ml-2 border-none bg-transparent"
            items={items}
            onSelect={onSelect}
            selectedKeys={[selectedKey]}
          />
        </Col>
        <Col className="flex flex-auto flex-col overflow-hidden p-4 pb-4 pt-0">
          <AssignmentGridView
            key={selectedKey}
            variant="material"
            target={selectedKey}
            trigger={triggerView}
            onAction={onAction}
            onSearch={onSearch}
          />
        </Col>
      </Row>
      <AssignmentFormDialog
        variant="material"
        target={selectedKey}
        open={dialogOpen}
        initialValues={dialogRecord}
        onFinish={onFinish}
        onCancel={onClose}
      />
      <AssignMaterialFormDialog
        open={assignOpen}
        onFinish={onAssignFinish}
        onCancel={onAssignClose}
      />
      <Modal
        open={previewOpen}
        title="Preview Targets"
        footer={null}
        width={760}
        destroyOnClose
        onCancel={() => setPreviewOpen(false)}
      >
        <MaterialTargetingPreview />
      </Modal>
      {contextHolder}
    </Col>
  );
}

export default WellbeingMaterialAssignmentsPage;
